import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { JwtService } from '@app/core/services/jwt/jwt-services/jwt-service';
import { UserConfigService } from '@app/core/services/user-config/user-config.service';
import { CustomSpinnerRequestHeader } from '@app/custom-service/spinner-interceptor.service';
import { PermissionsManagerService } from '@app/custom-shared/services/permissions-manager.service';
import { ProfileManagerService } from '@app/custom-shared/services/profile-manager.service';
import { MsalService } from '@azure/msal-angular';
import { CookieService } from 'ngx-cookie-service';
import { pipe, throwError } from 'rxjs';
import { take, tap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AuthGroup } from './authorization.types';
const BASE_PATH = environment.basePath;
const BASE_EXTERNALPI = environment.basePathExternalApi;
const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

@Injectable({
  providedIn: 'root',
})
export class CustomAuthenticationService {
  permissions: Array<string>;
  userLocation: UserLocation = new UserLocation();

  constructor(
    private _$http: HttpClient,
    private permissionsManager: PermissionsManagerService,
    private profileService: ProfileManagerService,
    private msalService: MsalService,
    private jwt: JwtService,
    private userConfig: UserConfigService,
    private cookiesService: CookieService
  ) { }

  async getUser(header?: CustomSpinnerRequestHeader): Promise<any> {
    if (!header) header = new CustomSpinnerRequestHeader(true);
    let response = this._$http.post<UserConfiguration>(`${BASE_PATH}/bff/master/loggedProfile`, null);
    response.pipe(take(1), tap((ev) => this.userConfig.basic = ev));
    return response.toPromise();
  }

  async updateJwt(): Promise<boolean> {
    let httpResponse = await this._$http.post<HttpResponse<any>>(`${BASE_PATH}/${this.jwt.endpoint}`, {}, { observe: 'response' }).toPromise();
    if (httpResponse && httpResponse.status == 200) {
      const cryptoKey = httpResponse.body["key"].toString();
      this.jwt.updateJwt(cryptoKey);
      return true;
    } else {
      return false;
    }
  }

  isMasterBO() {
    return this.profileService.isMasterBO();
  }

  isClienteBO() {
    return this.profileService.isClientBo();
  }

  isMasterCliente() {
    return this.profileService.isMasterClient();
  }

  setPermissions(response) {
    const { permissions, profiles } = response;
    const [profile] = profiles;

    this.permissionsManager.setPermissions(permissions);
    this.profileService.setProfile(profile);
    this.profileService.setClientBO(profile);
  }

  clearPermissions() {
    this.permissionsManager.setPermissions([]);
    this.profileService.clearProfile();
  }

  isLoggedIn() {
    if (this.msalService.instance.getAllAccounts()[0]) {
      return true;
    }
    return false;
  }

  getPermissions() {
    return this.permissionsManager.getPermissions().toPromise();
  }

  hasPermission(authGroup: AuthGroup) {
    return this.permissionsManager.hasNamePermission(authGroup);
  }

  hasPermissionAndType(authGroup: AuthGroup, type: string) {
    return this.permissionsManager.hasNamePermissionAndType(authGroup, type);
  }

  hasAllPermissions(permissions: Array<string>) {
    return this.permissionsManager.checkAllPermissons(permissions);
  }

  updateStatus() {
    return this._$http
      .get(`${BASE_PATH}/bff/users/login/first-access`)
      .toPromise();
  }

  // This method is called once and a list of permissions is stored in the permissions property
  initializePermissions() {
    const promise = new Promise<void>((resolve, reject) => {
      this.permissions = this.permissionsManager.getPermissions();
      this.permissions ? resolve() : reject()
    })
    return promise

  }

  logout() {
    this.msalService.logout();
    sessionStorage.clear();
    localStorage.clear();
    this.cookiesService.deleteAll('/','b2clogin.com');
  }

  acceptCookie(latitude: string, longitude: string) {

    return this._$http.post<any>(
      `${BASE_EXTERNALPI}/users/acceptCookies`,
      null,
      { headers: headers },
    );
  }

  getIpAddress() {
    return null;
  }

  toIsoString(date) {
    var tzo = -date.getTimezoneOffset(),
      dif = tzo >= 0 ? '+' : '-',
      pad = function (num) {
        var norm = Math.floor(Math.abs(num));
        return (norm < 10 ? '0' : '') + norm;
      };

    return date.getFullYear() +
      '-' + pad(date.getMonth() + 1) +
      '-' + pad(date.getDate())
  }
}

export interface Role {
  type: string;
  name: string;
}

export interface PermissionGroup {
  id: string;
  name: string;
}

export interface Permissions {
  id: string;
  name: string;
  type: string;
}

export interface UserConfiguration {
  id: string;
  name: string;
  status: string;
  profiles: string[];
  permissionGroups: PermissionGroup[];
  permissions: Permissions[];
  cookieAccepted: boolean;
  isCaceis: boolean;
  language: string;
}

export class UserLocation {
  ip: string;
  latitude: string;
  longitude: string;
}
